/* eslint-disable @typescript-eslint/no-explicit-any */
import { type SetStateAction, useState } from "react";

import Avatar from "@/design/components/Avatar";
import Search from "@/design/components/Search";
import useSearch from "@/utils/hooks/useSearch";
import Valkyrie, { type IValkyrieIcon, viFilterXmark } from "@sippy-platform/valkyrie";

import {
  AvatarGroup,
  Box,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
} from "@mui/material";

export default function MultiFilterDropdown({
  id,
  label,
  options = [],
  searchKeys,
  getOptionLabel,
  getOptionLabelSecondary,
  getOptionValue,
  getAvatarIcon,
  value,
  setValue,
  noAvatar,
  icon,
  limit,
}: {
  id: string;
  label: {
    all: string;
    singular: string;
    plural: string;
  };
  options: object[];
  searchKeys: string[];
  getOptionLabel: (object: object) => string;
  getOptionLabelSecondary?: (object: object) => string;
  getOptionValue: (object: object) => string;
  getAvatarIcon: (object: object) => IValkyrieIcon;
  value: (string | number)[];
  setValue: React.ActionDispatch<SetStateAction<any>>;
  noAvatar: boolean;
  icon: IValkyrieIcon;
  limit?: number;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
  const [needle, setNeedle] = useState("");
  const [internalValue, setInternalValue] = useState<(number | string)[]>(value);
  const valueKey = JSON.stringify(value);
  const [previousValueKey, setPreviousValueKey] = useState(valueKey);

  if (valueKey !== previousValueKey) {
    setPreviousValueKey(valueKey);
    setInternalValue(value);
  }

  const open = Boolean(anchorEl);

  const results = useSearch(options ?? [], needle, searchKeys);

  function closeFilter() {
    setAnchorEl(undefined);
    setNeedle("");
    setValue(internalValue);
  }

  const activeOptions = options.filter((option) => value.includes(getOptionValue(option)));

  function handleSelection(id: number | string | null) {
    if (id) {
      setInternalValue((prev) =>
        internalValue.indexOf(id) === -1 ? [...prev, id] : prev.filter((value) => value !== id),
      );
    } else {
      setInternalValue([]);
    }
  }

  return (
    <>
      <Button color="inherit" id={id} onClick={(event) => setAnchorEl(event.currentTarget)}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
          }}
        >
          {activeOptions.length >= 1 && !noAvatar ? (
            <AvatarGroup spacing={8}>
              {activeOptions.slice(0, activeOptions.length >= 4 ? 2 : 3).map((option, optionKey) => (
                <Avatar
                  key={optionKey}
                  text={getOptionLabel(option)}
                  icon={getAvatarIcon?.(option) ?? undefined}
                  px={24}
                />
              ))}
              {activeOptions.length >= 4 && <Avatar literal text={`+${activeOptions.length - 2}`} px={24} />}
            </AvatarGroup>
          ) : (
            <Valkyrie icon={icon} />
          )}
          <Box>
            {activeOptions.length === 0
              ? label.all
              : activeOptions.length === 1
                ? `1 ${label.singular}`
                : `${activeOptions.length} ${label.plural}`}
          </Box>
        </Stack>
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => closeFilter()}
        slotProps={{
          paper: {
            elevation: 1,
            sx: {
              border: "1px solid",
              borderColor: "divider",
              minWidth: 300,
            },
          },
        }}
      >
        {(searchKeys?.length ?? 0) >= 1 && (
          <Box sx={{ px: 1, pt: 1, position: "sticky", top: 0, zIndex: 100 }}>
            <Search blur id="search-accounts" accessKey="q" value={needle} onChange={setNeedle} />
          </Box>
        )}
        <List sx={{ p: 1, minWidth: 200 }}>
          <ListItem disablePadding sx={{ minHeight: 40 }}>
            <ListItemButton onClick={() => handleSelection(null)} sx={{ minHeight: 40 }}>
              <ListItemIcon>
                <Valkyrie icon={viFilterXmark} />{" "}
              </ListItemIcon>
              <ListItemText primary="Clear filter" />
            </ListItemButton>
          </ListItem>

          {results.map((option: object, optionKey: number) => (
            <ListItem key={optionKey} disablePadding sx={{ minHeight: 40 }}>
              <ListItemButton
                sx={{ minHeight: 40 }}
                onClick={() => handleSelection(getOptionValue(option))}
                selected={internalValue.includes(getOptionValue(option))}
                disabled={
                  !!(limit && internalValue?.length >= limit && !internalValue.includes(getOptionValue(option)))
                }
              >
                {!noAvatar && (
                  <ListItemAvatar sx={{ minWidth: 34 }}>
                    <Avatar text={getOptionLabel(option)} icon={getAvatarIcon?.(option) ?? undefined} px={24} />
                  </ListItemAvatar>
                )}
                <ListItemText
                  primary={getOptionLabel(option)}
                  secondary={getOptionLabelSecondary?.(option)}
                  sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "baseline" }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Popover>
    </>
  );
}
